/**
 * Copyright (C) 2026 Jugendstil, SLU - All Rights Reserved
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited. Proprietary and confidential.
 */
import { pageLocale } from '../internal/locale.js';
import { register } from '../registry.js';

const MILLISECONDS_PER_DAY = 86400000;

/**
 * Rewrites a timestamp as how far away it is: "In 2 weeks", "Yesterday", "3 months ago".
 *
 * `Intl.RelativeTimeFormat` is the same formatter iOS's `RelativeDateTimeFormatter` is, down to
 * saying "yesterday" rather than "1 day ago" where that is the idiom (`numeric: 'auto'`). Doing it
 * here rather than on the server is what keeps fifteen keys per language out of the resource
 * bundles and gets every language's plural rules right for nothing.
 *
 * The element carries the absolute date as its text, so a page with no script still says when the
 * thing is. Only the phrasing is enhanced, never the fact.
 *
 * The unit is the largest one that fits, which is what the platform formatters do: a fixture eleven
 * days out reads "in 2 weeks" and not "in 11 days". `Math.round` on the way up and `numeric: 'auto'`
 * on the way out, so nine days is "next week" rather than "in 1 week".
 */
export function paintRelativeTime(element: Element): void {
    const target = Date.parse(element.getAttribute('datetime') ?? '');
    if (Number.isNaN(target)) {
        return;
    }

    const locale = pageLocale();

    let formatter: Intl.RelativeTimeFormat;
    try {
        formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    } catch {
        /* An unknown language tag throws rather than falling back, and a row that says the date is
           better than a row that says nothing. */
        return;
    }

    const days = Math.round((target - Date.now()) / MILLISECONDS_PER_DAY);
    const absolute = Math.abs(days);

    let value = days;
    let unit: Intl.RelativeTimeFormatUnit = 'day';
    if (absolute >= 30) {
        value = Math.round(days / 30);
        unit = 'month';
    } else if (absolute >= 7) {
        value = Math.round(days / 7);
        unit = 'week';
    }

    const relative = formatter.format(value, unit);

    /* The formatters return a string built to read mid-sentence, and this one is a line of its
       own. Only the first letter, not the whole string. */
    element.textContent = relative.charAt(0).toLocaleUpperCase(locale) + relative.slice(1);
}

export function paint(root: ParentNode): void {
    root.querySelectorAll('[data-wp-relative-time]').forEach(paintRelativeTime);
}

export function listen(): void {
    register(paint);
}
